"use client";
import { useState, useEffect } from "react";
import { sendFeedback } from "..//../actions/sendFeeback";
import { toast as showToast } from "react-hot-toast";

// Simple toast component (for inline feedback)
function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  return (
    <div
      className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded shadow-lg text-white text-base font-semibold transition
        ${type === "success" ? "bg-green-600" : "bg-red-600"}`}
      role="alert"
    >
      {message}
      <button className="ml-4 text-white font-bold" onClick={onClose} aria-label="Затвори">
        ×
      </button>
    </div>
  );
}

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Math protection: generate two random numbers for the question (client only)
  const [mathA, setMathA] = useState<number | null>(null);
  const [mathB, setMathB] = useState<number | null>(null);

  useEffect(() => {
    setMathA(Math.floor(Math.random() * 10) + 1);
    setMathB(Math.floor(Math.random() * 10) + 1);
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setToast(null);
    setLoading(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = formData.get("name") as string;
    const lastname = formData.get("lastname") as string;
    const council = formData.get("council") as string;
    const content = formData.get("content") as string;
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const honeypot = formData.get("website") as string;
    const mathAnswer = formData.get("math") as string;

    // Honeypot check
    if (honeypot) {
      setToast({ message: "Бот засечен.", type: "error" });
      setLoading(false);
      return;
    }

    // Math check
    if (
      mathA === null ||
      mathB === null ||
      !mathAnswer ||
      Number(mathAnswer) !== mathA + mathB
    ) {
      setToast({ message: "Грешен отговор на антибот въпроса.", type: "error" });
      showToast.error("Грешен отговор на антибот въпроса.");
      setLoading(false);
      return;
    }

    // Check for missing required fields (all required, with translated messages)
    if (!name) {
      setToast({ message: "Моля, попълнете полето 'Име'.", type: "error" });
      showToast.error("Моля, попълнете полето 'Име'.");
      setLoading(false);
      return;
    }
    if (!lastname) {
      setToast({ message: "Моля, попълнете полето 'Фамилия'.", type: "error" });
      showToast.error("Моля, попълнете полето 'Фамилия'.");
      setLoading(false);
      return;
    }
    if (!council) {
      setToast({ message: "Моля, попълнете полето 'Общински съвет'.", type: "error" });
      showToast.error("Моля, попълнете полето 'Общински съвет'.");
      setLoading(false);
      return;
    }
    if (!email) {
      setToast({ message: "Моля, попълнете полето 'Имейл адрес'.", type: "error" });
      showToast.error("Моля, попълнете полето 'Имейл адрес'.");
      setLoading(false);
      return;
    }
    if (!phone) {
      setToast({ message: "Моля, попълнете полето 'Телефон'.", type: "error" });
      showToast.error("Моля, попълнете полето 'Телефон'.");
      setLoading(false);
      return;
    }
    if (!content) {
      setToast({ message: "Моля, попълнете полето 'Съдържание на запитването'.", type: "error" });
      showToast.error("Моля, попълнете полето 'Съдържание на запитването'.");
      setLoading(false);
      return;
    }

    const res = await sendFeedback({ name, lastname, council, content, email, phone });

    setLoading(false);
    if (res.success) {
      setToast({ message: "Вашето запитване беше изпратено успешно!", type: "success" });
      showToast.success("Вашето запитване беше изпратено успешно!");
      form.reset();
      // Regenerate math question after successful submit
      setMathA(Math.floor(Math.random() * 10) + 1);
      setMathB(Math.floor(Math.random() * 10) + 1);
    } else {
      setToast({ message: "Възникна грешка при изпращане. Моля, опитайте отново.", type: "error" });
      showToast.error("Възникна грешка при изпращане. Моля, опитайте отново.");
    }
  }

  return (
    <section id="contact" className="bg-white py-12 sm:py-16">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="max-w-lg mx-auto px-4 sm:px-0">
        <h2 className="text-2xl sm:text-3xl font-bold text-sky-600 mb-6 text-center">
          Свържете се с нас
        </h2>
        <div className="mb-4 text-red-600 text-center text-sm">
          Всички полета са задължителни.
        </div>
        <form className="space-y-4" onSubmit={handleSubmit} autoComplete="off">
          {/* Honeypot field */}
          <input
            type="text"
            name="website"
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
          />
          <div className="flex gap-2">
            <input
              name="name"
              className="w-1/2 border border-sky-200 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 text-base"
              type="text"
              placeholder="Име"
            />
            <input
              name="lastname"
              className="w-1/2 border border-sky-200 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 text-base"
              type="text"
              placeholder="Фамилия"
            />
          </div>
          <div className="flex gap-2">
            <input
              name="council"
              className="w-1/2 border border-sky-200 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 text-base"
              type="text"
              placeholder="Общински съвет"
            />
            <input
              name="email"
              className="w-1/2 border border-sky-200 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 text-base"
              type="email"
              placeholder="Имейл адрес"
            />
          </div>
          <input
            name="phone"
            className="w-full border border-sky-200 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 text-base"
            type="text"
            placeholder="Телефон"
          />
          <textarea
            name="content"
            className="w-full border border-sky-200 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 text-base"
            rows={4}
            placeholder="Съдържание на запитването, Брой общински съветници ... "
          />
          {/* Simple math question */}
          <div className="flex items-center gap-2">
            <label htmlFor="math" className="text-base text-gray-700">
              {mathA !== null && mathB !== null
                ? `Колко е ${mathA} + ${mathB}?`
                : "Зареждане..."}
            </label>
            <input
              name="math"
              id="math"
              className="border border-sky-200 rounded px-2 py-1 w-16 text-base"
              type="number"
              placeholder="?"
              autoComplete="off"
              disabled={mathA === null || mathB === null}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-sky-600 text-white font-semibold py-2 rounded hover:bg-sky-700 transition text-base sm:text-lg"
          >
            {loading ? "Изпращане..." : "Изпрати запитване"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;